import { OuterContentContainer } from "@themes/pollinator/ui/OuterContentContainer";
import { SectionHeading } from "@themes/pollinator/ui/SectionHeading";
import { Type } from "@themes/pollinator/ui/Type";
import { Avatar } from "@themes/pollinator/ui/Avatar";
import { Loader } from "@themes/pollinator/ui/Loader";
import { useFetchUserCircles } from "@core/features/circle";
import { usePlatformStore, usePublicProfileSheet } from "@platform/store";
import { PublicProfileSheetContainer } from "@platform/features/programProfile";
import type { UserWithProfile } from "@core/features/auth";

export type Props = {
  /** Program ID for the circle */
  programId: number;
  /** Whether the user has access to circle content */
  hasAccess: boolean;
};

/**
 * Circle tab content for the Program page.
 * Shows circle member list if user has access, otherwise shows not-matched message.
 */
export function CircleTabContent({ programId, hasAccess }: Props) {
  const user = usePlatformStore((s) => s.user);
  const { open: openProfileSheet } = usePublicProfileSheet();

  const { data: circlesData, isLoading } = useFetchUserCircles({
    user,
    programId,
    isEnabled: hasAccess && !!user,
  });

  const circles = circlesData?.circles ?? [];
  const members = circles.flatMap((circle) => circle.users ?? []);

  if (!hasAccess) {
    return (
      <OuterContentContainer className="flex flex-1" innerClassName="gap-8">
        <SectionHeading
          title="Your Peer Circle"
          description="Peer cohorts have not been matched yet. Check back soon to see who you matched with."
        />
      </OuterContentContainer>
    );
  }

  return (
    <>
      <OuterContentContainer className="flex flex-1" innerClassName="gap-8">
        <SectionHeading
          title="Your Peer Cohort"
          description="See who is in your peer cohort and click on a member to learn more about them via their profile."
        >
          {isLoading ? (
            <Loader />
          ) : members.length === 0 ? (
            <Type variant="body2" className="text-brand-700">
              No members found in your cohort yet.
            </Type>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {members.map((member: UserWithProfile) => (
                <button
                  key={member.id}
                  type="button"
                  onClick={() => openProfileSheet(member.id)}
                  className="flex flex-row gap-4 text-left hover:bg-brand-50 transition-colors cursor-pointer"
                >
                  <Avatar
                    image={member.profile_photo}
                    size={48}
                    alt={`${member.first_name} ${member.last_name}`}
                  />
                  <div className="flex flex-col gap-1 flex-1">
                    <Type variant="h5">
                      {member.first_name} {member.last_name}
                    </Type>
                    {member.pronouns && (
                      <Type variant="caption">{member.pronouns}</Type>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </SectionHeading>
      </OuterContentContainer>
      <PublicProfileSheetContainer programId={programId} />
    </>
  );
}

export default CircleTabContent;
